import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Medal, Sun, Moon } from "@phosphor-icons/react";

const VIEWS = {
  overall: { label: "Overall",     Icon: Trophy },
  day:     { label: "Day 6–18",    Icon: Sun },
  night:   { label: "Night 18–6",  Icon: Moon },
};

function pick(row, view) {
  if (view === "overall") {
    return { efficiency_pct: row.efficiency_pct, batches: row.batches,
             completed: row.completed, avg_yield: row.avg_yield, rejected: row.rejected };
  }
  return row[view];
}

export default function Operators() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("overall");

  useEffect(() => {
    const load = async () => {
      const shiftParam = view === "day" ? "day" : view === "night" ? "night" : "all";
      const { data } = await api.get(`/analytics/operator-performance?shift=${shiftParam}`);
      setRows(data);
    };
    load();
  }, [view]);

  const active = rows
    .map(r => ({ row: r, v: pick(r, view) }))
    .filter(x => x.v.batches > 0 || x.v.rejected > 0)
    .sort((a, b) => b.v.efficiency_pct - a.v.efficiency_pct);

  const podium = active.slice(0, 3);
  const maxEff = Math.max(...active.map(x => x.v.efficiency_pct), 1);
  const totalBatches = active.reduce((a, x) => a + x.v.batches, 0);

  const ViewIcon = VIEWS[view].Icon;

  return (
    <div data-testid="operators-page">
      <PageHeader subtitle="Performance" title="Operator Leaderboard"
        actions={
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              {Object.entries(VIEWS).map(([k, v]) => (
                <TabsTrigger key={k} value={k} data-testid={`shift-view-${k}`}>
                  <v.Icon size={14} className="mr-1.5"/>{v.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2 flex items-center gap-1.5"><ViewIcon size={11}/>Active in {VIEWS[view].label}</div><div className="text-metric text-3xl">{active.length}</div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2">Batches in Scope</div><div className="text-metric text-3xl text-primary">{totalBatches}</div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2">Best Efficiency</div><div className="text-metric text-3xl text-emerald-600">{active[0]?.v.efficiency_pct ?? 0}%</div></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><div className="overline mb-2">Avg Rejected</div><div className="text-metric text-3xl text-red-600">{active.length ? Math.round(active.reduce((a,x)=>a+x.v.rejected,0)/active.length) : 0}</div></CardContent></Card>
      </div>

      {podium.length >= 3 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {podium.map(({ row, v }, i) => {
            const medals = ["🥇", "🥈", "🥉"];
            const bgs = ["bg-amber-50 border-amber-200", "bg-slate-50 border-slate-200", "bg-orange-50 border-orange-200"];
            return (
              <Card key={row.name} className={`shadow-none ${bgs[i]}`} data-testid={`podium-${i+1}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-3xl">{medals[i]}</div>
                    <Badge variant="secondary" className="inline-flex items-center gap-1 uppercase text-[10px]">
                      <ViewIcon size={11}/>{VIEWS[view].label}
                    </Badge>
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight">{row.name}</div>
                  <div className="overline mb-3">{row.role?.replace("_"," ")}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><div className="overline">Efficiency</div><div className="text-metric text-xl text-emerald-700">{v.efficiency_pct}%</div></div>
                    <div><div className="overline">Batches</div><div className="text-metric text-xl">{v.batches}</div></div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Operator</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Batches</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Yield %</TableHead>
                <TableHead className="text-right">Rejected</TableHead>
                <TableHead className="min-w-[180px]">Efficiency ({VIEWS[view].label})</TableHead>
                <TableHead className="text-right w-24 border-l border-border">Day</TableHead>
                <TableHead className="text-right w-24">Night</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {active.map(({ row, v }, i) => (
                <TableRow key={row.name} data-testid={`operator-row-${i}`}>
                  <TableCell className="mono font-bold">{i+1}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {i < 3 && <Medal size={14} weight="fill" className={["text-amber-500","text-slate-400","text-orange-500"][i]}/>}
                      <span className="font-semibold">{row.name}</span>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="uppercase text-[10px]">{row.role?.replace("_"," ")}</Badge></TableCell>
                  <TableCell className="text-right mono">{v.batches}</TableCell>
                  <TableCell className="text-right mono">{v.completed}</TableCell>
                  <TableCell className="text-right mono">{v.avg_yield}%</TableCell>
                  <TableCell className="text-right mono">{v.rejected}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={(v.efficiency_pct / maxEff) * 100} className="h-1.5 flex-1"/>
                      <span className="mono text-xs w-12 text-right font-semibold">{v.efficiency_pct}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right mono text-xs border-l border-border">
                    <div className={row.day.efficiency_pct > 0 ? "text-foreground" : "text-muted-foreground/60"}>
                      {row.day.efficiency_pct}%
                    </div>
                    <div className="text-[10px] text-muted-foreground">{row.day.batches}b</div>
                  </TableCell>
                  <TableCell className="text-right mono text-xs">
                    <div className={row.night.efficiency_pct > 0 ? "text-foreground" : "text-muted-foreground/60"}>
                      {row.night.efficiency_pct}%
                    </div>
                    <div className="text-[10px] text-muted-foreground">{row.night.batches}b</div>
                  </TableCell>
                </TableRow>
              ))}
              {active.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No activity recorded in {VIEWS[view].label}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
